// Матч-центр (HQ-269): день — GET screens/match-day/ (адрес — config.match_center_sync.day_bundle_path), живой батч —
// GET screens/live/batch/ (by_date: date + timezone_offset; by_ids: ids видимых живых строк), точки календаря —
// GET screens/match-day/counts/. Форма — MLS-Backend apps/api_mobile_v5 (screens/match_day.py, match_center_payloads.py,
// screens/live.py). Пояс сервер принимает только «+HH:MM» (apps/core/timezone_offset.py: иное — 400
// invalid_timezone_offset), поэтому минуты устройства переводим в этот вид.
// Опрос: день — по poll.interval_seconds ответа (45 есть живые / 180 нет), батч — по live_batch_interval_seconds; в фоне
// оба стоят сами (src/api/queryClient.ts: focusManager ↔ Telegram activated/deactivated), при возврате — сразу запрос.
import { queryOptions, useQuery } from '@tanstack/react-query';
import { apiGet } from './client';
import type { AppConfig } from './config';
import type { EmptyState } from '../ui/States';

/** Имя сущности на языке ответа: сервер шлёт name_localized (config.display_name_field), запас — name. */
export interface NamedRef {
  id: number; name: string; name_localized?: string | null; logo?: string | null; image?: string | null; logo_thumb?: string | null;
}
export interface CountryRef extends NamedRef { code?: string | null; flag?: string | null; flag_url?: string | null }
export interface LeagueRef extends NamedRef { country?: CountryRef | null }

export interface Score {
  home: number | null; away: number | null; display?: string;
  penalty?: { home: number | null; away: number | null; display?: string } | null;
  winner_side?: 'home' | 'away' | null;
}

/** Строка матча дня (сжатый формат: команды и лига — id в словарях teams{} / leagues{}; полный — объектами). */
export interface DayMatch {
  id: number;
  kickoff_at?: string | null;
  /** короткий код статуса: NS, 1H, HT, 2H, FT, PST… */
  status: string;
  status_long?: string | null;
  status_long_localized?: string | null;
  is_live?: boolean | null;
  live_plausible?: boolean | null;
  minute?: number | null;
  minute_extra?: number | null;
  score?: Score | null;
  home_id?: number; away_id?: number; league_id?: number;
  home?: NamedRef | null; away?: NamedRef | null; league?: LeagueRef | null;
  kickoff_time_known?: boolean;
  kickoff_local_time?: string | null;
  kickoff_local_date?: string | null;
  [key: string]: unknown;
}

export interface DayLeague { league_id: number; match_count?: number; live_count?: number; matches: DayMatch[]; league?: LeagueRef }
/** Лига блока топ-лиг: карточка лиги целиком + матчи. */
export type TopLeague = LeagueRef & DayLeague;
export interface DayCountry extends CountryRef { match_count?: number; live_count?: number; leagues: DayLeague[] }

export interface DayPoll {
  interval_seconds?: number; live_batch_interval_seconds?: number; live_batch_path?: string; live_batch_mode?: string;
}

export interface MatchDayResponse {
  date: string;
  timezone_offset?: string;
  match_count?: number;
  live_count?: number;
  top_tournaments?: LeagueRef[];
  top_section?: { leagues?: TopLeague[]; match_count?: number } | null;
  countries?: DayCountry[];
  teams?: Record<string, NamedRef>;
  leagues?: Record<string, LeagueRef>;
  poll?: DayPoll;
  empty_state?: EmptyState | null;
}

/** Строка живого батча: статус — объектом (у дня — строкой). */
export interface LiveBatchRow {
  id: number;
  minute?: number | null;
  minute_extra?: number | null;
  status?: { short?: string | null; long?: string | null; long_localized?: string | null; elapsed?: number | null; extra?: number | null } | null;
  status_short?: string | null;
  status_long_localized?: string | null;
  is_live?: boolean | null;
  live_plausible?: boolean | null;
  score?: Score | null;
}
export interface LiveBatchResponse { results: LiveBatchRow[]; updated_at?: string }

export interface CountsResponse { from: string; to: string; days: Array<{ date: string; match_count: number; live_count?: number }> }

/** config.match_center_sync (views_config.py) — читаем то, чем пользуемся. */
export interface MatchCenterSync {
  day_bundle_path?: string; prefetch_days_back?: number; prefetch_days_forward?: number; live_filter_field?: string;
  live_batch_path?: string; live_batch_interval_seconds?: number; live_batch_mode?: string; top_league_ids?: number[];
}
export interface FeaturedTab {
  available?: boolean; league_id?: number; name_localized?: string; tab_title?: string; tab_label?: string; tab_icon_url?: string;
}

export const DAY_PATH = '/api/v5/mobile/screens/match-day/';
export const LIVE_BATCH_PATH = '/api/v5/mobile/screens/live/batch/';
export const COUNTS_PATH = '/api/v5/mobile/screens/match-day/counts/';
/** Запасные интервалы, если сервер не назвал (снимки до 09.2026 не несут poll.interval_seconds). */
export const DEFAULT_DAY_INTERVAL = 180;
export const DEFAULT_BATCH_INTERVAL = 15;

export const syncOf = (config: AppConfig | undefined) => (config?.match_center_sync ?? {}) as MatchCenterSync;
export const featuredOf = (config: AppConfig | undefined) => (config?.featured_tab ?? null) as FeaturedTab | null;

/** Пояс устройства «+HH:MM» из минут восточнее UTC (getTimezoneOffset даёт обратный знак). */
export function timezoneOffset(minutesEast: number = -new Date().getTimezoneOffset()): string {
  const sign = minutesEast < 0 ? '-' : '+';
  const abs = Math.abs(minutesEast);
  return `${sign}${String(Math.floor(abs / 60)).padStart(2, '0')}:${String(abs % 60).padStart(2, '0')}`;
}

const q = (params: Record<string, string>) => Object.entries(params).map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join('&');

export function dayUrl(sync: MatchCenterSync, date: string, tz: string): string {
  return `${sync.day_bundle_path || DAY_PATH}?${q({ date, timezone_offset: tz })}`;
}

export type BatchMode = 'by_date' | 'by_ids';
export function batchMode(sync: MatchCenterSync, poll: DayPoll | undefined): BatchMode {
  const mode = sync.live_batch_mode ?? poll?.live_batch_mode;
  return mode === 'by_ids' ? 'by_ids' : 'by_date';
}
export function batchInterval(sync: MatchCenterSync, poll: DayPoll | undefined): number {
  return sync.live_batch_interval_seconds ?? poll?.live_batch_interval_seconds ?? DEFAULT_BATCH_INTERVAL;
}
export function batchUrl(sync: MatchCenterSync, poll: DayPoll | undefined, mode: BatchMode, date: string, tz: string, ids: number[]): string {
  const path = poll?.live_batch_path || sync.live_batch_path || LIVE_BATCH_PATH;
  return mode === 'by_ids' ? `${path}?${q({ ids: ids.join(',') })}` : `${path}?${q({ date, timezone_offset: tz })}`;
}

export const dayKey = (date: string, tz: string) => ['match-day', date, tz] as const;

/** День: опрос по poll.interval_seconds ответа. */
export function dayQuery(sync: MatchCenterSync, date: string, tz: string) {
  return queryOptions({
    queryKey: dayKey(date, tz),
    queryFn: ({ signal }) => apiGet<MatchDayResponse>(dayUrl(sync, date, tz), signal),
    refetchInterval: (query) => (query.state.data?.poll?.interval_seconds ?? DEFAULT_DAY_INTERVAL) * 1000,
  });
}
export const useMatchDay = (sync: MatchCenterSync, date: string, tz: string) => useQuery(dayQuery(sync, date, tz));

/** Живой батч: только пока на экране есть живой матч. Первый запрос — через интервал после дня (initialData пустая и
 *  свежая на интервал): день только что принёс те же числа. Вернулись из фона — устаревший батч уходит сразу. */
export interface LiveBatchArgs {
  sync: MatchCenterSync; poll: DayPoll | undefined; date: string; tz: string; ids: number[]; enabled: boolean; dayUpdatedAt: number;
}
export function liveBatchQuery(opts: LiveBatchArgs) {
  const mode = batchMode(opts.sync, opts.poll);
  const interval = batchInterval(opts.sync, opts.poll) * 1000;
  const ids = mode === 'by_ids' ? [...opts.ids].sort((a, b) => a - b) : [];
  return queryOptions<LiveBatchResponse>({
    // снимок дня в ключе: каждый новый ответ дня начинает отсчёт интервала батча заново (initialData — на его момент)
    queryKey: ['live-batch', mode, opts.date, opts.tz, ids.join(','), opts.dayUpdatedAt],
    queryFn: ({ signal }) => apiGet<LiveBatchResponse>(batchUrl(opts.sync, opts.poll, mode, opts.date, opts.tz, ids), signal),
    enabled: opts.enabled && (mode === 'by_date' || ids.length > 0),
    initialData: { results: [] },
    initialDataUpdatedAt: opts.dayUpdatedAt,
    staleTime: interval,
    refetchInterval: interval,
    gcTime: interval * 2,
  });
}
export const useLiveBatch = (opts: LiveBatchArgs) => useQuery(liveBatchQuery(opts));

/** Точки календаря за показанный месяц (окно ≤ 62 дней). leagues — по каким лигам (гид §2.1-К: без отбора матчи есть
 *  каждый день и точка ничего не говорит); пусто — по всем. */
export function useMatchDayCounts(from: string, to: string, tz: string, leagues: number[], enabled: boolean) {
  const params: Record<string, string> = { from, to, timezone_offset: tz };
  if (leagues.length) params.leagues = leagues.join(',');
  return useQuery({
    queryKey: ['match-day-counts', from, to, tz, params.leagues ?? ''],
    queryFn: ({ signal }) => apiGet<CountsResponse>(`${COUNTS_PATH}?${q(params)}`, signal),
    enabled,
    staleTime: 10 * 60_000,
    refetchOnWindowFocus: false,
  });
}
